/**
 * Streem Authentication Type Definitions
 * Based on Leucine authentication system specifications
 */

// Enums

/**
 * Account Type Enum
 *
 * Note: Backend may return specific SSO provider types (e.g., "AZURE", "OKTA", "SAML")
 * Frontend treats any non-LOCAL type as SSO for flexibility
 */
export enum AccountType {
  LOCAL = "LOCAL",
  SSO = "SSO",
}

export enum LicenseState {
  INTIMATE = "INTIMATE",
  GRACE = "GRACE",
  GRACE_EXCEEDED = "GRACE_EXCEEDED",
  GENUINE = "GENUINE",
}

export enum LicenseWorkflow {
  NONE = "NONE",
  NOTIFICATION_UNBLOCKED = "NOTIFICATION_UNBLOCKED",
  NOTIFICATION_BLOCKED = "NOTIFICATION_BLOCKED",
}

export enum AuthErrorCode {
  INVALID_CREDENTIALS = "101003",
  PASSWORD_EXPIRED = "101004",
  REGISTRATION_TOKEN_EXPIRED = "101005",
  FORGOT_PASSWORD_TOKEN_EXPIRED = "101006",
  JWT_TOKEN_INVALID = "101101",
  JTI_TOKEN_REVOKED = "101102",
  JWT_REFRESH_TOKEN_EXPIRED = "101103",
  JWT_ACCESS_TOKEN_EXPIRED = "101104",
  JWT_TOKEN_EXPIRED = "101105",
  USER_SELF_LOCKED = "104006",
  USER_ARCHIVED = "104007",
  USER_INVITE_EXPIRED = "104010",
  USER_LOCKED = "104012",
  USER_ACCOUNT_LOCKED = "104014",
  SSO_INVALID_CREDENTIALS = "105001",
}

// Auto-logout error codes
export const AUTO_LOGOUT_CODES = [
  AuthErrorCode.JWT_TOKEN_INVALID,
  AuthErrorCode.JTI_TOKEN_REVOKED,
  AuthErrorCode.JWT_REFRESH_TOKEN_EXPIRED,
  AuthErrorCode.USER_SELF_LOCKED,
  AuthErrorCode.USER_ARCHIVED,
  AuthErrorCode.USER_LOCKED,
];

// Interfaces

export interface PasswordPolicy {
  minimumPasswordLength: number;
  minimumLowercaseCharacters: number;
  minimumUppercaseCharacters: number;
  minimumSpecialCharacters: number;
  minimumNumericCharacters: number;
}

export interface Facility {
  id: string;
  name: string;
  dateFormat: string;
  timeZone: string;
}

export interface License {
  days: number;
  expired: boolean;
  facilityId: string;
  graceEndsOn: string;
  renewalDate: string;
  state: LicenseState;
  workflow: LicenseWorkflow;
}

export interface AppSettings {
  logoUrl: string;
  sessionIdleTimeoutInMinutes: number;
  ssoType?: string;
  passwordPolicy: PasswordPolicy;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  employeeId?: string;
  roles: string[];
  facilities: Facility[];
  settings: AppSettings;
  licenses: License[];
}

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  data: T | null;
  errors: ApiError[] | null;
}

// Account Lookup

export interface AccountLookupRequest {
  username: string;
}

export interface AccountLookupResponse {
  type: AccountType;
  username: string;
  redirectUrl?: string; // Only for SSO
}

// Login

export interface LoginRequest {
  username: string;
  password: string | null; // null for SSO
  code?: string; // SSO authorization code
}

export interface LoginResponse {
  id: string;
  firstName: string;
  lastName: string;
  message: string;
  accessToken: string;
  refreshToken: string;
  idToken?: string; // SSO ID token for logout
  roles: string[];
  facilities: Facility[];
  settings: AppSettings;
  licenses: License[];
}

// Re-Login

export interface ReLoginRequest {
  username: string;
  accessToken: string;
  password?: string; // For LOCAL users
  code?: string; // SSO authorization code
}

// Token Refresh

export interface TokenRefreshRequest {
  accessToken: string;
  refreshToken: string;
}

export interface TokenRefreshResponse {
  accessToken: string;
}

// Logout

export interface LogoutRequest {
  idToken?: string; // Required for SSO logout
}

export interface LogoutResponse {
  logoutUrl?: string; // SSO logout URL
}
